import type { ConsumptionMonthlyData } from 'types'

import { useEffect } from 'react'
import styled from 'styled-components'

import { clamp } from 'utils'
import copy from 'copy.json'

import Badge from 'pages/matches/Badge'

const texts = copy.components.consumption_details.year_summary

type Props = {
  hoveredItem: number
  entry: ConsumptionMonthlyData
}

export default function Tooltip({ hoveredItem, entry }: Props) {
  const moveTooltip = (hoveredItem: number) => {
    const tooltip = document.querySelector('.tooltipConsumption') as HTMLElement
    const barsWrapper = document.querySelector('.barsConsumption') as HTMLElement
    const arrow = document.querySelector('.arrowConsumption') as HTMLElement
    const bars = document.querySelectorAll('.barConsumption') as NodeListOf<HTMLElement>
    const bar = bars[hoveredItem]

    if (!tooltip || !barsWrapper || !bar) return

    const margin = 24
    const offset = 16

    const barRect = bar.getBoundingClientRect()
    const barsWrapperRect = barsWrapper.getBoundingClientRect()
    const tooltipRect = barsWrapper.getBoundingClientRect()

    const leftEdge = barRect.x + bar.offsetWidth / 2 - tooltip.offsetWidth / 2
    const topEdge = barsWrapperRect.y - tooltip.offsetHeight - offset

    const tooltipLeft = clamp(margin, leftEdge, window.innerWidth - tooltip.offsetWidth - margin)
    const tooltipTop = clamp(offset, topEdge, window.innerHeight - tooltip.offsetHeight - offset)
    const arrowLeft = clamp(tooltipRect.x, barRect.x, tooltipRect.x + tooltipRect.width - margin)
    const arrowTop = tooltipTop + tooltip.offsetHeight

    tooltip.style.left = `${tooltipLeft}px`
    tooltip.style.top = `${tooltipTop}px`
    arrow.style.left = `${arrowLeft}px`
    arrow.style.top = `${arrowTop}px`
  }

  useEffect(() => moveTooltip(hoveredItem), [hoveredItem])

  return (
    <StyledTooltip className="tooltipConsumption dark">
      <Total>
        <p>{texts.total_consumption}</p>
        <Badge>
          {entry.total.toFixed(0)} {copy.common.MWh}
        </Badge>
      </Total>

      <Row>
        {Object.entries(entry.zone_data).map(([_, value], index) => (
          <Column key={index}>
            <Badge secondary border>
              {texts.zone} {index + 1}
            </Badge>

            <span className="zoneDescriptionTooltip">
              {texts.consumption[Object.keys(entry.zone_data).length - 1][index]}
            </span>

            <Badge
              morningConsumption={index === 0}
              afternoonConsumption={index === 1}
              restConsumption={index === 2}
            >
              {value.consumption.toFixed(0)} {copy.common.MWh}
            </Badge>

            <Badge border>{value.participating_percentage.toFixed(0)}%</Badge>
          </Column>
        ))}
      </Row>

      <Arrow className="arrowConsumption" />
    </StyledTooltip>
  )
}

const StyledTooltip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  max-width: calc(100% - 48px);
  padding: 16px;
  background: var(--dark-gray);
  border-radius: 4px;
  white-space: nowrap;
  position: fixed;
  z-index: 1;
  transition: left 0.2s;
`

const Arrow = styled.span`
  width: 24px;
  height: 24px;
  background: inherit;
  border-radius: 2px;
  position: fixed;
  z-index: -1;
  transform: translateY(-18px) rotate(45deg);
  transition: left 0.2s;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;

  > span:not(${Badge}) {
    font: var(--600-12);
  }

  .zoneDescriptionTooltip {
    font: var(--600-12);
    color: var(--gray);
  }
`

const Total = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font: var(--600-13);

  ${Badge} {
    font: var(--600-24);
    background-color: var(--white);
    color: var(--dark-gray);
  }
`
